import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const layers = [
  {
    title: '可直接迁移的技术能力',
    icon: 'ri-settings-3-line',
    color: 'primary' as const,
    items: ['长视频切片', '语音转写', '课件与画面对齐', '提问与回应识别', '时间分析', '证据链', '报告生成'],
  },
  {
    title: '需按学科重新配置',
    icon: 'ri-edit-2-line',
    color: 'accent' as const,
    items: ['教学目标', '评价指标', '教材知识库', '学科知识图谱', '指标权重', '专家样本', '分档标准'],
  },
  {
    title: '需专项模型或工具',
    icon: 'ri-tools-line',
    color: 'secondary' as const,
    items: ['公式推导', '实验过程', '外语输出', '医学操作规范', '艺术与体育动作'],
  },
];

export default function P80ThreeQuestions({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-24 right-24 w-96 h-96 rounded-full bg-primary-100/10 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-[28rem] h-[28rem] rounded-full bg-accent-100/8 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-10">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />跨学科迁移
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从思政课程群到其他学科：<span className="text-gold-600">哪些能迁移，哪些需重建</span>
          </h2>
          <p className={`text-sm md:text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            平台能力可以通用，学科评价不能通用；技术可以迁移，专业标准必须重建
          </p>
        </div>

        {/* Three Layers */}
        <div className={`max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="space-y-3 md:space-y-4">
            {layers.map((layer, li) => (
              <div
                key={layer.title}
                className={`p-4 md:p-5 glass-liquid rounded-xl`}
                style={{ transitionDelay: `${300 + li * 150}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl ${layer.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : layer.color === 'secondary' ? 'bg-secondary-100/60 border border-secondary-200/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                    <i className={`${layer.icon} text-lg ${layer.color === 'accent' ? 'text-gold-600' : layer.color === 'secondary' ? 'text-secondary-600' : 'text-primary-600'}`} />
                  </div>
                  <span className={`text-sm md:text-base font-heading font-bold ${layer.color === 'accent' ? 'text-gold-700' : layer.color === 'secondary' ? 'text-secondary-700' : 'text-primary-700'}`}>
                    第{li + 1}层 · {layer.title}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {layer.items.map((item) => (
                    <span
                      key={item}
                      className={`px-3 py-1.5 rounded-full text-xs font-body font-semibold ${
                        layer.color === 'accent'
                          ? 'bg-gold-100/80 text-gold-700 border border-gold-300/30'
                          : layer.color === 'secondary'
                          ? 'bg-secondary-100/60 text-secondary-700 border border-secondary-200/30'
                          : 'bg-primary-100/60 text-primary-700 border border-primary-200/30'
                      }`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conclusion */}
        <div className={`max-w-3xl mx-auto text-center mb-5 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="p-5 glass-gold border border-gold-200/40 rounded-xl">
            <p className="font-heading text-base md:text-lg font-bold text-gold-800 mb-2">
              不是一套模型评价所有学科，而是一套平台承载不同学科模型
            </p>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-500 font-body leading-relaxed">
              五门高校思政课程已有天津评价数据支撑；其他学科属于可迁移方向，仍需从小规模样本开始验证。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}